import { useState, useEffect, useCallback, useRef } from 'react';
import type { TaxScenario, TaxScenarioInput, TaxResults } from '@/tax/types';
import { calculateTaxServer } from '@/tax/taxApi';
import { reportCalcFailure } from '@/lib/calcFailure';
import { solveRothForAlt, solveRothToBracket, BracketSolverUnavailableError } from '@/tax/solveVariant';
import { toast } from 'sonner';
import { bracketReading } from '@/lib/comparisonStory';
import { formatCurrency, formatPercent } from '@/tax/format';
import { HPE_DEDUCTION_MULTIPLIER, CHARITABLE_NONCASH_AGI_CAP } from '@/tax/uiConstants';
import { FIA_STRATEGIES, fiaAnnualizedReturn, fiaTermYears, type RothGrowthModel } from '@/tax/rothGrowth';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useStrategyEntitlements } from '@/hooks/useStrategyEntitlements';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { DollarInput } from '../fields';
import { RothPlanBlock } from './RothPlanBlock';
import { defaultRothPlan, type RothPlanSettings } from '@/tax/rothPlan';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Loader2, Plus, X, ChevronDown, ChevronUp } from 'lucide-react';

// ── Types ────────────────────────────────────────────────────────────────────

export interface HpeConfig {
  enabled: boolean;
  amount: number;
  deductionMultiplier: number;
  conservativeMode: boolean;
}

export interface RothConfig {
  enabled: boolean;
  conversionAmount: number;
  growthRate: number;
  growthYears: number;
  /** Growth projection model. 'straight' uses growthRate/growthYears; 'fiaN' uses a fixed 10-year FIA sequence. */
  model: RothGrowthModel;
}

export interface StrategyVariant {
  id: string;
  /** Original Supabase scenario ID — present when this variant maps to an existing DB row */
  scenarioId?: string;
  hpe: HpeConfig;
  roth: RothConfig;
  /** LTCG override for this variant. undefined = inherit from baseline. number = concrete override. */
  longTermCapitalGains?: number;
  /** Auto-max Alt from the Roth/LTCG inputs. Mutually exclusive with autoSolveRoth. */
  autoMaxAlt?: boolean;
  /** Auto-solve Roth to make the entered Alt fit the 50% AGI non-cash ceiling. Mutually exclusive with autoMaxAlt. */
  autoSolveRoth?: boolean;
  /** Multi-year Roth plan settings (`roth_plan` flag). Undefined until the advisor switches it on. */
  rothPlan?: RothPlanSettings;
  results?: TaxResults;
  calculating?: boolean;
}

export interface StrategyBuilderStepProps {
  input: TaxScenarioInput;
  variants: StrategyVariant[];
  onVariantsChange: (variants: StrategyVariant[]) => void;
  baselineResults?: TaxResults | null;
  baselineCalculating?: boolean;
  /** When true, strategy knobs are disabled and Add/Remove are hidden. Used when editing the baseline so advisors can see variants recalc live but can't reshape them. */
  readOnly?: boolean;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function makeDefaultVariant(): StrategyVariant {
  return {
    id: crypto.randomUUID(),
    hpe: { enabled: false, amount: 0, deductionMultiplier: HPE_DEDUCTION_MULTIPLIER, conservativeMode: false },
    roth: { enabled: false, conversionAmount: 0, growthRate: 0.06, growthYears: 8, model: 'straight' },
  };
}

export function variantAutoName(variant: StrategyVariant): string {
  const parts: string[] = [];
  if (variant.roth.enabled) {
    if (variant.roth.model === 'fia1') parts.push('FIA 1');
    else if (variant.roth.model === 'fia2') parts.push('FIA 2');
    else if (variant.roth.model === 'fia3') parts.push('FIA 3');
    else parts.push('Roth');
  }
  if (variant.hpe.enabled) parts.push('Alt');
  if (variant.longTermCapitalGains !== undefined) parts.push('LTCG');
  return parts.length > 0 ? parts.join(' + ') : 'Strategy';
}

/**
 * Suffix to write when re-saving an EXISTING variant scenario. A stored
 * "(...)" suffix that differs from the auto-name of the config it was saved
 * with is an advisor's nickname (dashboard pencil) — it survives the re-save.
 * Otherwise the fresh auto-name is used, so strategy edits keep relabeling
 * variants that were never nicknamed.
 */
export function resolveVariantSuffix(storedName: string | undefined, originalAutoName: string, freshAutoName: string): string {
  const m = storedName?.match(/\(([^)]+)\)\s*$/);
  const storedSuffix = m ? m[1] : null;
  return storedSuffix && storedSuffix !== originalAutoName ? storedSuffix : freshAutoName;
}

/**
 * Reconstruct a StrategyVariant from a stored variant scenario row. Used by both the wizard's
 * variant reconstruction and the strategy-edit sheet.
 *
 * When `baseline` is provided, LTCG is stored as `undefined` (inherit) when the scenario's
 * LTCG equals the baseline's — preserving the "baseline edit ripples to variants" behavior.
 * When it differs, we store the concrete override. Callers without easy baseline access may
 * omit the argument; LTCG will then always be stored concretely.
 */
export function scenarioToVariant(scenario: TaxScenario, baseline?: TaxScenario): StrategyVariant {
  const i = scenario.input;
  const baselineLtcg = baseline?.input?.longTermCapitalGains;
  const ltcg = i.longTermCapitalGains ?? 0;
  const ltcgOverride = baseline && ltcg === (baselineLtcg ?? 0) ? undefined : ltcg;
  return {
    id: crypto.randomUUID(),
    scenarioId: scenario.id,
    hpe: {
      enabled: (i.alternativeInvestmentAmount ?? 0) > 0,
      amount: i.alternativeInvestmentAmount ?? 0,
      deductionMultiplier: i.hpeDeductionMultiplier ?? HPE_DEDUCTION_MULTIPLIER,
      conservativeMode: i.hpeConservativeMode ?? false,
    },
    roth: {
      enabled: (i.rothConversionAmount ?? 0) > 0,
      conversionAmount: i.rothConversionAmount ?? 0,
      growthRate: i.rothGrowthRate ?? 0.06,
      growthYears: i.rothGrowthYears ?? 8,
      model: i.rothGrowthModel ?? 'straight',
    },
    longTermCapitalGains: ltcgOverride,
    autoMaxAlt: i.uiState?.autoMaxAlt ?? false,
    autoSolveRoth: i.uiState?.autoSolveRoth ?? false,
    rothPlan: i.uiState?.rothPlan,
    results: scenario.results,
  };
}

/** Build the TaxScenarioInput for a variant by merging base + overrides */
export function variantToInput(base: TaxScenarioInput, variant: StrategyVariant, clientName: string, suffix: string): TaxScenarioInput {
  return {
    ...base,
    name: suffix ? `${clientName} (${suffix})` : clientName,
    alternativeInvestmentAmount: variant.hpe.enabled ? variant.hpe.amount : 0,
    hpeDeductionMultiplier: variant.hpe.deductionMultiplier,
    hpeConservativeMode: variant.hpe.conservativeMode,
    rothConversionAmount: variant.roth.enabled ? variant.roth.conversionAmount : base.rothConversionAmount,
    rothGrowthRate: variant.roth.growthRate,
    rothGrowthYears: variant.roth.growthYears,
    rothGrowthModel: variant.roth.model,
    longTermCapitalGains: variant.longTermCapitalGains ?? base.longTermCapitalGains,
    uiState: {
      autoMaxAlt: !!variant.autoMaxAlt,
      autoSolveRoth: !!variant.autoSolveRoth,
      // Written only when the plan exists, so a strategy saved without one carries no new key.
      ...(variant.rothPlan ? { rothPlan: variant.rothPlan } : {}),
    },
  };
}

// ── Single Variant Card ───────────────────────────────────────────────────────

const DEBOUNCE_MS = 400;

export function VariantCard({
  base,
  variant,
  index,
  baselineResults,
  onUpdate,
  onRemove,
  readOnly = false,
}: {
  base: TaxScenarioInput;
  variant: StrategyVariant;
  index: number;
  baselineResults?: TaxResults | null;
  onUpdate: (v: StrategyVariant) => void;
  onRemove: () => void;
  readOnly?: boolean;
}) {
  const [hpeOpen, setHpeOpen] = useState(variant.hpe.enabled);
  const [rothOpen, setRothOpen] = useState(variant.roth.enabled);
  const [ltcgOpen, setLtcgOpen] = useState(variant.longTermCapitalGains !== undefined);
  // Remembers the user's last selected FIA strategy so the toggle off→on cycle returns to it
  // instead of defaulting back to fia1 every time.
  const lastFiaRef = useRef<Exclude<RothGrowthModel, 'straight'>>(
    variant.roth.model !== 'straight' ? variant.roth.model : 'fia1'
  );
  // Two gates, both must open:
  //   useFeatureFlags   — does the FIA product feature exist for this user at all
  //                       ('fia_growth_strategies': global / org pin / user pin).
  //   useStrategyEntitlements — has the upline LICENSED FIA / Alts for this user
  //                       (the Team Management switches, deepest-explicit-wins).
  // When FIA is off by either gate, the Roth card renders identically to its pre-FIA
  // shape: editable Growth Rate and Growth Years inputs, no FIA toggle / Select. The
  // engine still understands rothGrowthModel from existing data — only the UI
  // affordance is hidden.
  //
  // The licence only stops a lever being ADDED (Drew, 2026-09-22). A strategy that
  // already carries an Alt or an FIA model keeps its section exactly as it was —
  // editable, recalculable, duplicable — so switching a team off never strands a
  // client someone already built. "Already carries" is judged when the card opens,
  // so toggling the Alt off and back on inside the same edit doesn't make the
  // section vanish under the advisor's cursor.
  const { isEnabled } = useFeatureFlags();
  const { entitlements } = useStrategyEntitlements();
  const carriedFiaAtOpen = useRef(variant.roth.model !== 'straight').current;
  const carriedAltAtOpen = useRef(variant.hpe.enabled).current;
  const fiaEnabled = isEnabled('fia_growth_strategies')
    && (entitlements.fia || carriedFiaAtOpen || variant.roth.model !== 'straight');
  // Alts have no product-existence flag — the licence switch is the only gate.
  const showAltSection = entitlements.alts || carriedAltAtOpen || variant.hpe.enabled;
  // Fill-to-bracket UI is gated behind the 'fill_to_bracket' flag (default off,
  // rolled out per-org from SuperAdmin → Feature Flags). The solve-roth edge
  // function's bracket mode is NOT gated — only the UI affordance is hidden.
  const fillToBracketEnabled = isEnabled('fill_to_bracket');
  // The bracket meter is correct but reads oddly on big capital-gains clients
  // ("$1.8M taxable" beside "Ordinary income · 22%"), so it ships dark until
  // it has been walked through with Andrew and Dylan (Drew, 2026-09-22).
  const bracketMeterEnabled = isEnabled('bracket_meter');
  // Multi-year Roth plan settings, off for everyone until `roth_plan` is pinned.
  const rothPlanEnabled = isEnabled('roth_plan');
  // Multi-year on: the plan is the whole Roth section. Every single-conversion
  // control is a one-year version of something the plan asks across years, so
  // none of them render beside it (Drew, 2026-10-23).
  const planOn = rothPlanEnabled && variant.roth.enabled && !!variant.rothPlan?.enabled;
  const [localResults, setLocalResults] = useState<TaxResults | null>(null);
  const [calculating, setCalculating] = useState(false);
  const [localHpeAmount, setLocalHpeAmount] = useState(variant.hpe.amount);
  // null when no solver attempt has finished yet or solver succeeded; object when the requested Alt cannot be reached by any Roth below the ceiling.
  const [noSolution, setNoSolution] = useState<{ requested: number; achievable: number } | null>(null);
  // Fill-to-bracket controls (ephemeral UI state — not persisted with the variant).
  const [fillOpen, setFillOpen] = useState(false);
  const [fillBracketRate, setFillBracketRate] = useState('0.24');
  const [fillBuffer, setFillBuffer] = useState(0);
  const [fillingBracket, setFillingBracket] = useState(false);
  const [bracketNoRoom, setBracketNoRoom] = useState<string | null>(null);

  const c = formatCurrency;
  const p = formatPercent;

  // Effective LTCG: variant override if set, else baseline.
  const effectiveLtcg = variant.longTermCapitalGains ?? base.longTermCapitalGains;

  // Compute HPE limits — use server-computed max when available, baseline-derived as fallback.
  // Mirrors the engine's §170(b) post-OBBBA capacity: other PROPERTY gifts (each category
  // at its own cap) eat the 50% pool before the HPE, and cash gifts — which stack toward
  // the 60% overall ceiling — reduce the max only past the 10%-of-AGI cushion
  // (min(50% pool, 60% − cash)), since beyond that each HPE dollar displaces cash.
  const fallbackAgi = baselineResults?.adjustedGrossIncome ?? 0;
  const fallbackPool = fallbackAgi * CHARITABLE_NONCASH_AGI_CAP;
  const fallbackOtherCharity =
    Math.min(base.nonCashPropertyPublicCharity, fallbackPool) +
    Math.min(base.propertyGiftsPrivateFoundation, fallbackAgi * 0.3) +
    Math.min(base.capitalGainPropertyPublicCharity, fallbackAgi * 0.3) +
    Math.min(base.capitalGainPropertyPrivateFoundation, fallbackAgi * 0.2);
  const fallbackCashCeiling = fallbackAgi * 0.6 - Math.min(Math.max(0, base.cashGiftsPublicCharity), fallbackAgi * 0.6);
  const fallbackMaxDeduction = Math.max(0, Math.min(fallbackPool, fallbackCashCeiling) - fallbackOtherCharity);
  const fallbackMaxInvestment = Math.floor(fallbackMaxDeduction / variant.hpe.deductionMultiplier);
  const maxInvestment = localResults?.hpeMaxInvestment ?? fallbackMaxInvestment;
  const effectiveHpeAmount = maxInvestment > 0 ? Math.min(localHpeAmount, maxInvestment) : localHpeAmount;
  // Only flag over-cap against a real engine result. Before the first server
  // recalc lands, maxInvestment is the baseline-derived fallback, which knows
  // nothing about the strategy's Roth lifting AGI — flagging against it makes
  // the red warning flash on open and then vanish for perfectly valid strategies.
  const isOverCap = !!localResults && localHpeAmount > maxInvestment && maxInvestment > 0;

  // Recalc is debounced ~1s; only show the IRR once a real fund-schedule result has landed
  // (conservative returns capital only, so its IRR is 0 by construction and not shown).
  const showIRR = !variant.hpe.conservativeMode && (localResults?.hpeFutureValue ?? 0) > localHpeAmount;

  // Recalculate whenever variant config changes
  useEffect(() => {
    if (!variant.hpe.enabled && !variant.roth.enabled) {
      setLocalResults(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setCalculating(true);
      try {
        const variantInput: TaxScenarioInput = {
          ...base,
          longTermCapitalGains: effectiveLtcg,
          alternativeInvestmentAmount: variant.hpe.enabled ? localHpeAmount : 0,
          hpeDeductionMultiplier: variant.hpe.deductionMultiplier,
          hpeConservativeMode: variant.hpe.conservativeMode,
          rothConversionAmount: variant.roth.enabled ? variant.roth.conversionAmount : base.rothConversionAmount,
          rothGrowthRate: variant.roth.growthRate,
          rothGrowthYears: variant.roth.growthYears,
          rothGrowthModel: variant.roth.model,
        };
        const result = await calculateTaxServer(variantInput);
        if (cancelled) return;
        setLocalResults(result);
        // Auto-max Alt: snap Alt input to the computed max after each recalc.
        // Safe because Alt does not feed back into hpeMaxInvestment, so this converges in one bounce.
        if (variant.autoMaxAlt && variant.hpe.enabled && result.hpeMaxInvestment !== localHpeAmount) {
          setLocalHpeAmount(result.hpeMaxInvestment);
          onUpdate({ ...variant, hpe: { ...variant.hpe, amount: result.hpeMaxInvestment }, results: result });
        } else {
          onUpdate({ ...variant, results: result });
        }
      } catch (err) {
        // Keep the previous results on screen, but say why they didn't update.
        if (!cancelled) reportCalcFailure(err, 'strategy');
      } finally {
        if (!cancelled) setCalculating(false);
      }
    }, DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    variant.hpe.enabled, variant.hpe.amount, variant.hpe.deductionMultiplier,
    variant.hpe.conservativeMode,
    variant.roth.enabled, variant.roth.conversionAmount,
    variant.roth.growthRate, variant.roth.growthYears, variant.roth.model,
    variant.longTermCapitalGains, variant.autoMaxAlt,
    base.filingStatus, base.state, base.wages, base.spouseWages,
    base.interestIncome, base.ordinaryDividends, base.grossSocialSecurity,
    base.pensionSum, base.shortTermCapitalGains, base.longTermCapitalGains,
    base.schedCGrossReceipts, base.schedCTotalExpenses,
    base.medicalExpenses, base.saltStateLocal, base.saltRealEstate,
    base.homeMortgageInterest, base.cashGiftsPublicCharity,
    base.nonCashPropertyPublicCharity, base.propertyGiftsPrivateFoundation,
    base.capitalGainPropertyPublicCharity, base.capitalGainPropertyPrivateFoundation,
    base.federalWithholding, base.estimatedTaxPayments, base.stateWithholding,
    base.numberOfQualifyingChildren, localHpeAmount, effectiveLtcg,
  ]);

  // Auto-solve Roth: when enabled, solve for the Roth conversion that makes the entered Alt fit the 50% AGI ceiling.
  // Runs SERVER-SIDE (the engine is never shipped to the browser); solveRothForAlt posts to the solve-roth
  // edge function. One round-trip per trigger. Cancellation guards against a stale result overwriting a newer one.
  // Deps intentionally exclude rothConversionAmount: the solver's own write to Roth must not re-enter the solver.
  useEffect(() => {
    if (!variant.autoSolveRoth || !variant.hpe.enabled || !variant.roth.enabled) {
      setNoSolution(null);
      return;
    }
    if (localHpeAmount <= 0) {
      setNoSolution(null);
      return;
    }
    const probe: TaxScenarioInput = {
      ...base,
      longTermCapitalGains: effectiveLtcg,
      alternativeInvestmentAmount: localHpeAmount,
      hpeDeductionMultiplier: variant.hpe.deductionMultiplier,
      hpeConservativeMode: variant.hpe.conservativeMode,
      rothConversionAmount: variant.roth.conversionAmount,
      rothGrowthRate: variant.roth.growthRate,
      rothGrowthYears: variant.roth.growthYears,
    };
    let cancelled = false;
    void (async () => {
      try {
        const solved = await solveRothForAlt(probe);
        if (cancelled) return;
        if (solved.roth === null) {
          setNoSolution({ requested: localHpeAmount, achievable: solved.maxAchievableAlt });
          return;
        }
        setNoSolution(null);
        if (solved.roth !== variant.roth.conversionAmount) {
          onUpdate({ ...variant, roth: { ...variant.roth, conversionAmount: solved.roth } });
        }
      } catch {
        // Solver round-trip failed (network/auth) — leave the current Roth untouched
        // and don't surface a false "no solution". The user can still edit manually.
        if (!cancelled) setNoSolution(null);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    variant.autoSolveRoth, variant.hpe.enabled, variant.roth.enabled,
    variant.hpe.deductionMultiplier,
    localHpeAmount, variant.longTermCapitalGains, effectiveLtcg,
    base.filingStatus, base.state, base.wages, base.spouseWages,
    base.interestIncome, base.ordinaryDividends, base.grossSocialSecurity,
    base.pensionSum, base.shortTermCapitalGains, base.longTermCapitalGains,
    base.schedCGrossReceipts, base.schedCTotalExpenses,
    base.medicalExpenses, base.saltStateLocal, base.saltRealEstate,
    base.homeMortgageInterest, base.cashGiftsPublicCharity,
    base.nonCashPropertyPublicCharity, base.propertyGiftsPrivateFoundation,
    base.capitalGainPropertyPublicCharity, base.capitalGainPropertyPrivateFoundation,
    base.federalWithholding, base.estimatedTaxPayments, base.stateWithholding,
    base.numberOfQualifyingChildren,
  ]);

  const updateHpe = useCallback((patch: Partial<HpeConfig>) => {
    onUpdate({ ...variant, hpe: { ...variant.hpe, ...patch } });
  }, [variant, onUpdate]);

  const updateRoth = useCallback((patch: Partial<RothConfig>) => {
    onUpdate({ ...variant, roth: { ...variant.roth, ...patch } });
  }, [variant, onUpdate]);

  const handleToggleHpe = (val: boolean) => {
    setHpeOpen(val);
    updateHpe({ enabled: val });
  };

  const handleToggleRoth = (val: boolean) => {
    setRothOpen(val);
    updateRoth({ enabled: val });
  };

  const handleHpeAmountChange = (val: number) => {
    setLocalHpeAmount(val);
    // Typing into the Alt field clears autoMaxAlt (user is taking over).
    const patch: Partial<StrategyVariant> = { hpe: { ...variant.hpe, amount: val } };
    if (variant.autoMaxAlt) patch.autoMaxAlt = false;
    onUpdate({ ...variant, ...patch });
  };

  // Alt input no longer auto-clips on blur. The engine still caps hpeImpliedDeduction at the 50% AGI
  // ceiling in the math (engine.ts:757), so over-max values stay numerically safe while preserving
  // the advisor's intent — they can then enable Auto-solve to widen the ceiling, or manually reduce.

  const handleRothAmountChange = (val: number) => {
    // Typing into the Roth field clears autoSolveRoth (user is taking over).
    const patch: Partial<StrategyVariant> = { roth: { ...variant.roth, conversionAmount: val } };
    if (variant.autoSolveRoth) patch.autoSolveRoth = false;
    onUpdate({ ...variant, ...patch });
  };

  // Fill-to-bracket: one round-trip to the solve-roth edge function (mode selected by the
  // `bracket` body key). Solves from a $0-conversion baseline — the button REPLACES whatever
  // conversion is typed with the largest amount that keeps ordinary taxable income at or
  // below (bracket top − buffer). Never writes on "no room" so a typed amount isn't clobbered.
  // With Auto-max Alt on, the solver re-maxes the Alt at every probe and hands back the Alt
  // it scored, which is written with the conversion — otherwise the recalc's re-snap would
  // grow the deduction and drag the fill well under the target.
  const handleFillToBracket = async () => {
    setFillingBracket(true);
    setBracketNoRoom(null);
    try {
      const probe: TaxScenarioInput = {
        ...base,
        longTermCapitalGains: effectiveLtcg,
        alternativeInvestmentAmount: variant.hpe.enabled ? localHpeAmount : 0,
        hpeDeductionMultiplier: variant.hpe.deductionMultiplier,
        hpeConservativeMode: variant.hpe.conservativeMode,
        rothConversionAmount: 0,
        rothGrowthRate: variant.roth.growthRate,
        rothGrowthYears: variant.roth.growthYears,
        rothGrowthModel: variant.roth.model,
      };
      const autoMaxAlt = !!variant.autoMaxAlt && variant.hpe.enabled;
      const solved = await solveRothToBracket(probe, {
        rate: Number(fillBracketRate),
        bufferUsd: fillBuffer,
        ...(autoMaxAlt ? { autoMaxAlt: true } : {}),
      });
      const pct = `${Math.round(Number(fillBracketRate) * 100)}%`;
      if (solved.roth === null || solved.roth === 0) {
        setBracketNoRoom(
          `No room — ordinary taxable income is already ${formatCurrency(solved.ordinaryTaxableIncome)} at $0 conversion vs the ${formatCurrency(solved.ceiling)} target (top of ${pct}${fillBuffer > 0 ? ' minus buffer' : ''}).`
        );
        return;
      }
      // Writing the solved amount takes over the Roth field, same as typing into it.
      const patch: Partial<StrategyVariant> = { roth: { ...variant.roth, conversionAmount: solved.roth } };
      if (variant.autoSolveRoth) patch.autoSolveRoth = false;
      if (autoMaxAlt && typeof solved.altAmount === 'number') {
        setLocalHpeAmount(solved.altAmount);
        patch.hpe = { ...variant.hpe, amount: solved.altAmount };
      }
      onUpdate({ ...variant, ...patch });
    } catch (err) {
      toast.error(
        err instanceof BracketSolverUnavailableError
          ? err.message
          : 'Fill to bracket failed — check your connection and try again.'
      );
    } finally {
      setFillingBracket(false);
    }
  };

  const handleLtcgChange = (val: number) => {
    onUpdate({ ...variant, longTermCapitalGains: val });
  };

  const handleLtcgReset = () => {
    onUpdate({ ...variant, longTermCapitalGains: undefined });
  };

  // Mutually exclusive auto toggles: turning one on turns the other off.
  const handleToggleAutoMaxAlt = (val: boolean) => {
    onUpdate({ ...variant, autoMaxAlt: val, autoSolveRoth: val ? false : variant.autoSolveRoth });
  };

  const handleToggleAutoSolveRoth = (val: boolean) => {
    onUpdate({ ...variant, autoSolveRoth: val, autoMaxAlt: val ? false : variant.autoMaxAlt });
  };

  const taxSavings = localResults && baselineResults
    ? baselineResults.totalTaxLiability - localResults.totalTaxLiability
    : null;
  const autoName = variantAutoName(variant);

  return (
    <Card className="border">
      <CardContent className="p-5 space-y-4">
        {/* Card header */}
        <div className="flex items-center justify-between" data-tour="strategy-card-header">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-muted-foreground">Scenario {index + 2}</span>
            {autoName !== 'Strategy' && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                {autoName}
              </span>
            )}
            {calculating && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
          </div>
          {!readOnly && (
            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={onRemove}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Income Overrides Section — variant-scoped LTCG override */}
        <div className="rounded-lg border overflow-hidden" data-tour="ltcg-section">
          <div
            className={`flex items-center justify-between px-4 py-3 select-none ${readOnly ? '' : 'cursor-pointer'} ${variant.longTermCapitalGains !== undefined ? 'bg-primary/5' : 'bg-muted/20'}`}
            onClick={readOnly ? undefined : () => setLtcgOpen(!ltcgOpen)}
          >
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Income Overrides</span>
              {variant.longTermCapitalGains !== undefined && variant.longTermCapitalGains !== base.longTermCapitalGains && (
                <span className="text-[10px] text-muted-foreground font-mono">LTCG overridden</span>
              )}
            </div>
            {ltcgOpen
              ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
              : <ChevronDown className="h-4 w-4 text-muted-foreground" />
            }
          </div>

          {ltcgOpen && (
            <div className="p-4 space-y-4 border-t">
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium text-muted-foreground">Long-Term Capital Gains ($)</Label>
                  {variant.longTermCapitalGains !== undefined && !readOnly && (
                    <button
                      type="button"
                      onClick={handleLtcgReset}
                      className="text-[10px] text-primary hover:underline"
                    >
                      Reset to baseline
                    </button>
                  )}
                </div>
                <DollarInput
                  value={variant.longTermCapitalGains}
                  onValueChange={handleLtcgChange}
                  blankWhenZero={false}
                  className="h-9 text-sm font-mono"
                  placeholder={base.longTermCapitalGains ? `Baseline: ${base.longTermCapitalGains.toLocaleString('en-US')}` : '0'}
                  disabled={readOnly}
                />
                <p className="text-[10px] text-muted-foreground">
                  Harvesting LTCG raises AGI, which widens the 50% non-cash charitable ceiling for Alt.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Roth Section */}
        <div className="rounded-lg border overflow-hidden" data-tour="roth-section">
          <div
            className={`flex items-center justify-between px-4 py-3 select-none ${readOnly ? '' : 'cursor-pointer'} ${variant.roth.enabled ? 'bg-primary/5' : 'bg-muted/20'}`}
            onClick={readOnly ? undefined : () => handleToggleRoth(!variant.roth.enabled)}
          >
            <span className="text-sm font-medium">Roth Conversion</span>
            <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
              {rothPlanEnabled && variant.roth.enabled && (
                <label className="flex items-center gap-1.5 cursor-pointer mr-2" data-tour="roth-multi-year">
                  <Checkbox
                    checked={!!variant.rothPlan?.enabled}
                    disabled={readOnly}
                    className="h-3.5 w-3.5"
                    aria-label="Multi-year"
                    onCheckedChange={v => onUpdate({
                      ...variant,
                      rothPlan: { ...(variant.rothPlan ?? defaultRothPlan(base.taxYear)), enabled: v === true },
                    })}
                  />
                  <span className="text-xs font-medium">Multi-year</span>
                </label>
              )}
              <Switch checked={variant.roth.enabled} onCheckedChange={handleToggleRoth} disabled={readOnly} />
              {variant.roth.enabled
                ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                : <ChevronDown className="h-4 w-4 text-muted-foreground" />
              }
            </div>
          </div>

          {(variant.roth.enabled || rothOpen) && (() => {
            // When the flag is off, force the UI into straight mode regardless of stored model.
            // Engine math is unaffected; this only hides the FIA UI affordances.
            const isFia = fiaEnabled && variant.roth.model !== 'straight';
            const fiaModel = isFia ? (variant.roth.model as Exclude<RothGrowthModel, 'straight'>) : null;
            const fiaRate = fiaModel ? fiaAnnualizedReturn(fiaModel) : null;
            const fiaYears = fiaModel ? fiaTermYears(fiaModel) : null;
            if (planOn && variant.rothPlan) {
              return (
                <div className="p-4 border-t">
                  <RothPlanBlock
                    base={base}
                    plan={variant.rothPlan}
                    readOnly={readOnly}
                    onChange={plan => onUpdate({ ...variant, rothPlan: plan })}
                    growth={{
                      rate: variant.roth.growthRate,
                      model: fiaEnabled ? variant.roth.model : 'straight',
                      fiaOffered: fiaEnabled,
                      onRate: rate => updateRoth({ growthRate: rate }),
                      onModel: model => { if (model !== 'straight') lastFiaRef.current = model; updateRoth({ model }); },
                    }}
                  />
                </div>
              );
            }
            return (
            <div className="p-4 space-y-4 border-t">
              {fiaEnabled && (
                <div className="rounded-md border bg-muted/20 p-3 space-y-2" data-tour="roth-fia">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-xs font-medium">Use FIA Strategy</Label>
                      <p className="text-[10px] text-muted-foreground">
                        Project growth over a 10-year indexed-annuity sequence instead of a flat rate.
                      </p>
                    </div>
                    <Switch
                      checked={isFia}
                      disabled={readOnly}
                      onCheckedChange={on => {
                        if (on) {
                          updateRoth({ model: lastFiaRef.current });
                        } else {
                          if (variant.roth.model !== 'straight') {
                            lastFiaRef.current = variant.roth.model as Exclude<RothGrowthModel, 'straight'>;
                          }
                          updateRoth({ model: 'straight' });
                        }
                      }}
                    />
                  </div>
                  {isFia && (
                    <Select
                      value={variant.roth.model}
                      onValueChange={v => {
                        const m = v as Exclude<RothGrowthModel, 'straight'>;
                        lastFiaRef.current = m;
                        updateRoth({ model: m });
                      }}
                      disabled={readOnly}
                    >
                      <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fia1">{FIA_STRATEGIES.fia1.label}</SelectItem>
                        <SelectItem value="fia2">{FIA_STRATEGIES.fia2.label}</SelectItem>
                        <SelectItem value="fia3">{FIA_STRATEGIES.fia3.label}</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-medium text-muted-foreground">Conversion Amount ($)</Label>
                    {variant.roth.enabled && variant.hpe.enabled && !readOnly && (
                      <label className="flex items-center gap-1.5 cursor-pointer" data-tour="roth-autosolve">
                        <Checkbox
                          checked={!!variant.autoSolveRoth}
                          onCheckedChange={v => handleToggleAutoSolveRoth(v === true)}
                          className="h-3.5 w-3.5"
                        />
                        <span className="text-[10px] text-muted-foreground">Auto-solve to fit Alt</span>
                      </label>
                    )}
                  </div>
                  <DollarInput
                    value={variant.roth.conversionAmount}
                    onValueChange={handleRothAmountChange}
                    className="h-9 text-sm font-mono"
                    placeholder="0"
                    disabled={readOnly || (!!variant.autoSolveRoth && variant.hpe.enabled && variant.roth.enabled)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground">
                    {isFia ? 'Annualized Return (%)' : 'Growth Rate (%)'}
                  </Label>
                  {isFia ? (
                    <p className="h-9 flex items-center font-mono text-sm font-bold">
                      {((fiaRate ?? 0) * 100).toFixed(2)}%
                    </p>
                  ) : (
                    <Input
                      type="number"
                      step="0.01"
                      value={(variant.roth.growthRate * 100).toFixed(1)}
                      onChange={e => updateRoth({ growthRate: (Number(e.target.value) || 0) / 100 })}
                      className="h-9 text-sm font-mono"
                      placeholder="6"
                      disabled={readOnly}
                    />
                  )}
                </div>
                <div className="space-y-1.5" data-tour="roth-growth-years">
                  <Label className="text-xs font-medium text-muted-foreground">Growth Years</Label>
                  {isFia ? (
                    <p className="h-9 flex items-center font-mono text-sm font-bold">{fiaYears}</p>
                  ) : (
                    <Input
                      type="number"
                      step={1}
                      min={0}
                      value={variant.roth.growthYears || ''}
                      // Whole years only: the engine rejects 8.5 (validation.ts yearCount).
                      onChange={e => updateRoth({ growthYears: Math.max(0, Math.round(Number(e.target.value))) || 0 })}
                      className="h-9 text-sm font-mono"
                      placeholder="8"
                      disabled={readOnly}
                    />
                  )}
                </div>
              </div>

              {fillToBracketEnabled && !readOnly && variant.roth.enabled && (
                <div className="rounded-md border bg-muted/20 p-3 space-y-2" data-tour="roth-fill-bracket">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <Checkbox
                      checked={fillOpen}
                      onCheckedChange={v => {
                        setFillOpen(v === true);
                        if (v !== true) setBracketNoRoom(null);
                      }}
                      className="h-3.5 w-3.5"
                    />
                    <span className="text-xs font-medium">Fill to Bracket</span>
                  </label>
                  {fillOpen && (
                    <>
                      <p className="text-[10px] text-muted-foreground">
                        Solve for the largest conversion that keeps ordinary taxable income inside the chosen
                        federal bracket. Optional buffer stops short of the top by that amount.
                      </p>
                      <div className="flex items-end gap-2">
                        <div className="space-y-1 w-24">
                          <Label className="text-[10px] font-medium text-muted-foreground">Bracket</Label>
                          <Select value={fillBracketRate} onValueChange={setFillBracketRate}>
                            <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0.12">12%</SelectItem>
                              <SelectItem value="0.22">22%</SelectItem>
                              <SelectItem value="0.24">24%</SelectItem>
                              <SelectItem value="0.32">32%</SelectItem>
                              <SelectItem value="0.35">35%</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1 w-28">
                          <Label className="text-[10px] font-medium text-muted-foreground">Buffer ($)</Label>
                          <DollarInput
                            value={fillBuffer}
                            onValueChange={setFillBuffer}
                            className="h-9 text-sm font-mono"
                            placeholder="0"
                          />
                        </div>
                        <Button size="sm" className="h-9" onClick={handleFillToBracket} disabled={fillingBracket}>
                          {fillingBracket ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Fill'}
                        </Button>
                      </div>
                      {bracketNoRoom && (
                        <p className="text-[10px] text-destructive">{bracketNoRoom}</p>
                      )}
                    </>
                  )}
                </div>
              )}

              {localResults && variant.roth.enabled && variant.roth.conversionAmount > 0 && (
                <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Roth Future Value</p>
                    <p className="font-mono text-sm font-bold text-primary">{c(localResults.rothFutureValue)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Est. Future Tax Avoided</p>
                    <p className="font-mono text-sm font-bold">{c(localResults.estimatedFutureTaxAvoided)}</p>
                  </div>
                </div>
              )}
            </div>
            );
          })()}
        </div>

        {/* HPE Section — for users whose upline has licensed Alts, and on any
            strategy that already carries one */}
        {showAltSection && (
        <div className="rounded-lg border overflow-hidden" data-tour="alt-section">
          <div
            className={`flex items-center justify-between px-4 py-3 select-none ${readOnly ? '' : 'cursor-pointer'} ${variant.hpe.enabled ? 'bg-primary/5' : 'bg-muted/20'}`}
            onClick={readOnly ? undefined : () => handleToggleHpe(!variant.hpe.enabled)}
          >
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Alt Investment</span>
              {variant.hpe.enabled && maxInvestment > 0 && (
                <span className="text-[10px] text-muted-foreground font-mono">Max: {c(maxInvestment)}</span>
              )}
            </div>
            <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
              <Switch checked={variant.hpe.enabled} onCheckedChange={handleToggleHpe} disabled={readOnly} />
              {variant.hpe.enabled
                ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                : <ChevronDown className="h-4 w-4 text-muted-foreground" />
              }
            </div>
          </div>

          {(variant.hpe.enabled || hpeOpen) && (
            <div className="p-4 space-y-4 border-t">
              {noSolution && (
                <Alert variant="destructive" className="py-2">
                  <AlertTitle className="text-xs font-bold">
                    No Roth amount achieves {c(noSolution.requested)} Alt
                  </AlertTitle>
                  <AlertDescription className="text-xs">
                    Max achievable Alt with the hunted Roth bound: {c(noSolution.achievable)}. Lower the Alt target or raise LTCG to widen the ceiling.
                  </AlertDescription>
                </Alert>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-medium text-muted-foreground">Investment Amount ($)</Label>
                    {variant.hpe.enabled && variant.roth.enabled && !readOnly && (
                      <label className="flex items-center gap-1.5 cursor-pointer" data-tour="alt-automax">
                        <Checkbox
                          checked={!!variant.autoMaxAlt}
                          onCheckedChange={v => handleToggleAutoMaxAlt(v === true)}
                          className="h-3.5 w-3.5"
                        />
                        <span className="text-[10px] text-muted-foreground">Auto-max from Roth</span>
                      </label>
                    )}
                  </div>
                  <DollarInput
                    value={localHpeAmount}
                    onValueChange={handleHpeAmountChange}
                    className="h-9 text-sm font-mono"
                    placeholder="0"
                    disabled={readOnly || (!!variant.autoMaxAlt && variant.hpe.enabled && variant.roth.enabled)}
                  />
                  {isOverCap && !variant.autoMaxAlt && !variant.autoSolveRoth && (
                    <p className="text-xs text-destructive">
                      Exceeds 50% AGI ceiling (max {c(maxInvestment)}). Enable Auto-solve to widen, or reduce Alt.
                    </p>
                  )}
                </div>
                <div className="space-y-1.5" data-tour="alt-deduction">
                  <Label className="text-xs font-medium text-muted-foreground">Implied Deduction</Label>
                  <p className="h-9 flex items-center font-mono text-sm font-bold text-primary">
                    {c(effectiveHpeAmount * variant.hpe.deductionMultiplier)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2" data-tour="alt-conservative">
                <Switch
                  checked={variant.hpe.conservativeMode}
                  onCheckedChange={val => updateHpe({ conservativeMode: val })}
                  disabled={readOnly}
                  className="scale-75"
                />
                <span className="text-[10px] text-muted-foreground">Conservative</span>
              </div>

              {localResults && baselineResults && variant.hpe.enabled && effectiveHpeAmount > 0 && (
                <div className={`grid grid-cols-2 ${showIRR ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-3 pt-2 border-t`}>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Tax Savings</p>
                    <p className="font-mono text-sm font-bold text-emerald-600 dark:text-emerald-400">{c(taxSavings ?? 0)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Total Cash to Client</p>
                    <p className="font-mono text-sm font-bold">{c(localResults.hpeFutureValue)}</p>
                  </div>
                  {showIRR && (
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">IRR</p>
                      <p className="font-mono text-sm font-bold">{p(localResults.hpeIRR)}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Net Benefit</p>
                    <p className="font-mono text-sm font-bold text-emerald-600 dark:text-emerald-400">
                      {c((taxSavings ?? 0) + localResults.hpeFutureValue - effectiveHpeAmount)}
                    </p>
                  </div>
                </div>
              )}

              {localResults && localResults.amtOwed > 0 && (
                <Alert variant="destructive" className="py-2">
                  <AlertTitle className="text-xs font-bold">
                    {baselineResults && baselineResults.amtOwed === 0
                      ? `AMT Created — ${c(localResults.amtOwed)}`
                      : `AMT Triggered — ${c(localResults.amtOwed)}`}
                  </AlertTitle>
                  <AlertDescription className="text-xs">
                    {baselineResults && baselineResults.amtOwed === 0
                      ? "This strategy creates new AMT exposure. Review before recommending."
                      : "This has been factored into the total tax."}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>
        )}

        {/* Variant summary */}
        {localResults && baselineResults && (variant.hpe.enabled || variant.roth.enabled) && (
          <div className="rounded-lg bg-muted/30 px-4 py-2.5 text-sm space-y-2" data-tour="strategy-summary">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">AGI</span>
              <span className="font-mono font-bold">{c(localResults.adjustedGrossIncome)}</span>
              <span className="text-muted-foreground">Taxable Income</span>
              <span className="font-mono font-bold">{c(localResults.taxableIncome)}</span>
              {taxSavings !== null && taxSavings !== 0 && (
                <span className={`text-xs font-semibold font-mono ml-2 ${taxSavings > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive'}`}>
                  {taxSavings > 0 ? `Saves ${c(taxSavings)}` : `Costs ${c(-taxSavings)} more`}
                </span>
              )}
            </div>
            {bracketMeterEnabled && <BracketMeter results={localResults} />}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Bracket meter ────────────────────────────────────────────────────────────

/**
 * How full the federal bracket holding the strategy's ordinary taxable income is.
 * The bracket bounds come back from the engine with the result — the bracket table
 * itself stays server-side. Renders nothing for a result without them.
 *
 * The left label names the ORDINARY slice on purpose. It used to read "24% bracket",
 * which an advisor reads as "this client is in the 24% bracket" — true only when the
 * client has no preferential income. Put capital gains in the picture and the two
 * readings split: an alt client with 250k wages, 600k LTCG and a 200k alt has the
 * whole of their ordinary income wiped out by the charitable deduction, so the slice
 * is 0 and the old label announced "10% bracket" to someone owing $73k (Andrew's
 * in-person meeting, 2026-09-22). The number was always right; the sentence was not.
 * The better the alt works on a client with gains, the more certainly it read 10%.
 */
export function BracketMeter({ results }: { results: TaxResults }) {
  // One rule with the comparison page's bracket cell (src/lib/comparisonStory.ts),
  // so the card and the comparison can never disagree about a client's bracket.
  const reading = bracketReading(results);
  if (!reading) return null;
  const { ordinaryTaxableIncome, ordinaryBracketFloor, ordinaryBracketCeiling } = results;
  const { pct, left: leftLabel, right: rightLabel, aria: ariaLabel } = reading;
  const topBracket = reading.kind === 'top';
  const noOrdinary = reading.kind === 'none';
  const nearTop = reading.kind === 'in' && pct >= 90;

  return (
    <div className="space-y-1" data-testid="bracket-meter">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium">{leftLabel}</span>
        <span className={`font-mono ${nearTop ? 'text-amber-600 dark:text-amber-400 font-semibold' : 'text-muted-foreground'}`}>
          {rightLabel}
        </span>
      </div>
      <Progress
        value={pct}
        aria-label={ariaLabel}
        className={`h-2 ${nearTop ? '[&>div]:bg-amber-500' : ''}`}
      />
      <div className="flex justify-between text-[10px] font-mono text-muted-foreground">
        {/* No bracket bounds when there is no ordinary income: $0 and the 10% top would
            put the 10% bracket right back on the card. */}
        <span>{noOrdinary ? '' : formatCurrency(ordinaryBracketFloor)}</span>
        <span>Ordinary taxable {formatCurrency(ordinaryTaxableIncome)}</span>
        <span>{noOrdinary || topBracket ? '' : formatCurrency(ordinaryBracketCeiling)}</span>
      </div>
    </div>
  );
}

// ── Main StrategyBuilderStep ──────────────────────────────────────────────────

export default function StrategyBuilderStep({ input, variants, onVariantsChange, baselineResults, baselineCalculating, readOnly = false }: StrategyBuilderStepProps) {
  const c = formatCurrency;
  const p = formatPercent;

  const handleUpdate = useCallback((id: string, updated: StrategyVariant) => {
    onVariantsChange(variants.map(v => v.id === id ? updated : v));
  }, [variants, onVariantsChange]);

  const handleRemove = useCallback((id: string) => {
    onVariantsChange(variants.filter(v => v.id !== id));
  }, [variants, onVariantsChange]);

  const handleAdd = () => {
    onVariantsChange([...variants, makeDefaultVariant()]);
  };

  return (
    <div className="space-y-5">
      {/* Baseline card */}
      <Card className="border bg-muted/30" data-tour="baseline-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-3 mb-3">
            <h3 className="text-base font-bold">{input.name || 'Unnamed Client'}</h3>
            <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
              {input.filingStatus} · {input.state}
            </span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-secondary/20 text-secondary-foreground font-medium">
              Baseline
            </span>
            {baselineCalculating && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
          </div>
          {/* Dena, 2026-10-13: she imports the return, lands here and sizes the Roth
              conversion on the card right below against this one. The number that job
              needs is taxable income, the one the bracket is measured against — not
              total tax. The meter itself stays on the strategy card's strip (Drew:
              it already shows down there, no need for it up here too). */}
          {baselineResults ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">AGI</p>
                <p className="font-mono font-bold">{c(baselineResults.adjustedGrossIncome)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Taxable Income</p>
                <p className="font-mono font-bold">{c(baselineResults.taxableIncome)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Effective Rate</p>
                <p className="font-mono font-bold">{p(baselineResults.effectiveTaxRate)}</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Calculating baseline…</p>
          )}
        </CardContent>
      </Card>

      {/* Variant cards */}
      {variants.map((variant, i) => (
        <VariantCard
          key={variant.id}
          base={input}
          variant={variant}
          index={i}
          baselineResults={baselineResults}
          onUpdate={updated => handleUpdate(variant.id, updated)}
          onRemove={() => handleRemove(variant.id)}
          readOnly={readOnly}
        />
      ))}

      {/* Add scenario button — hidden during baseline edit; strategies are added/edited from the dashboard */}
      {!readOnly && (
        <Button
          type="button"
          variant="outline"
          className="w-full gap-2 border-dashed"
          onClick={handleAdd}
          data-tour="add-strategy"
        >
          <Plus className="h-4 w-4" />
          Add Strategy to Compare
        </Button>
      )}

      {!readOnly && variants.length === 0 && (
        <p className="text-center text-sm text-muted-foreground pb-2">
          Add at least one scenario above, or click "Save &amp; Calculate" to save baseline only.
        </p>
      )}

      {readOnly && variants.length > 0 && (
        <p className="text-center text-xs text-muted-foreground pb-2">
          Strategies shown for reference. Edit a strategy from the dashboard to change its parameters.
        </p>
      )}
    </div>
  );
}
